import { AlertTriangle, ArrowLeft, Hash, MoreHorizontal, Pencil, Plus, Trash2, Users, X } from "lucide-react";
import * as React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Page, PageHeader } from "@/components/layout/page";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/ui/feedback";
import { Avatar } from "@/components/ui/misc";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { AddPeopleDialog, TeamFormDialog } from "@/features/team-dialogs";
import { UserSkillsDialog } from "@/features/user-skills";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { linePurposeText } from "@/lib/line";
import {
  useDeleteTeam, useRemoveTeamLine, useRemoveTeamMember, useSkills, useTeam, useTeams, useUsersById,
} from "@/lib/queries";
import { leadNames, missingSkills, missingText, onLineCount, personName, teamSummary } from "@/lib/teams";
import { atLeast, type Team, type TeamLine, type TeamPerson } from "@/lib/types";

/**
 * Teams (platform ADR-185): a group of people with a lead, and the campaign lines it staffs.
 *
 * Supervisors and admins make teams and choose who is in them. A team is put on a campaign's line
 * from the campaign's Staffing tab, an admin's job as staffing is, and everyone in it with a phone
 * is on that line. Skills stay on each person: the team page shows them, flags one a line asks for
 * and a member lacks, and opens the same skills window as Users.
 */
export function TeamsPage() {
  const { user: actor } = useAuth();
  const navigate = useNavigate();
  const { data, isPending, error, refetch } = useTeams();
  const [making, setMaking] = React.useState(false);

  const newButton = atLeast(actor?.role, "supervisor") ? (
    <Button variant="default" onClick={() => setMaking(true)}>
      <Plus />
      New team
    </Button>
  ) : undefined;

  return (
    <Page>
      <PageHeader
        title="Teams"
        description="A team is a group of people with a lead. Put a team on a campaign's line, from the campaign's Staffing tab, and everyone in it takes that line's calls. Skills stay on each person."
        action={newButton}
      />
      <Card className="overflow-hidden">
        {isPending ? (
          <TableSkeleton cols={5} />
        ) : error ? (
          <ErrorState
            error={error}
            title="Could not load the teams"
            action={
              <Button variant="outline" onClick={() => void refetch()}>
                Try again
              </Button>
            }
          />
        ) : data.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No teams yet"
            description="Make one for each group that works together: the closers, the setters of a campaign. A campaign's Staffing tab can also make one from the reps already on a line."
            action={newButton}
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Team</TableHead>
                <TableHead>Lead</TableHead>
                <TableHead>People</TableHead>
                <TableHead>Staffs</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((t) => (
                <TableRow
                  key={t.id}
                  className="cursor-pointer [&>td]:h-12"
                  onClick={() => navigate(`/teams/${t.id}`)}
                >
                  <TableCell>
                    <Link
                      to={`/teams/${t.id}`}
                      className="inline-flex items-center gap-1.5 font-medium text-text hover:text-accent-11"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Hash className="size-3.5 text-text-muted" aria-hidden />
                      {t.name}
                    </Link>
                  </TableCell>
                  <TableCell className="text-text-muted">{leadNames(t) ?? "—"}</TableCell>
                  <TableCell>
                    <span className="flex items-center gap-1">
                      {t.members.slice(0, 6).map((m) => (
                        <Avatar key={m.user_id} name={personName(m)} className="size-6" />
                      ))}
                      <span className="pl-1 text-xs text-text-muted">{t.members.length}</span>
                    </span>
                  </TableCell>
                  <TableCell className="text-xs text-text-muted">{staffsText(t)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
      <TeamFormDialog
        open={making}
        onOpenChange={setMaking}
        onMade={(team) => navigate(`/teams/${team.id}`)}
      />
    </Page>
  );
}

/** "lhpark_ca-closer, harborpoint_ca-closer", or that it staffs nothing. */
function staffsText(team: Team): string {
  if (team.lines.length === 0) return "No line yet";
  return team.lines.map((l) => `${l.campaign_name ?? l.queue_name} · ${linePurposeText(l.purpose).label}`).join(", ");
}

export function TeamPage() {
  const { id } = useParams();
  const teamId = Number(id);
  const { user: actor } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const team = useTeam(teamId);
  const catalog = useSkills({ staleTime: 5 * 60_000 });
  const removeMember = useRemoveTeamMember();
  const removeLine = useRemoveTeamLine();
  const deleteTeam = useDeleteTeam();
  const [editing, setEditing] = React.useState(false);
  const [adding, setAdding] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [leaving, setLeaving] = React.useState<TeamPerson | null>(null);
  const [unstaffing, setUnstaffing] = React.useState<TeamLine | null>(null);
  const [skillsOf, setSkillsOf] = React.useState<number | null>(null);
  const [skillsOpen, setSkillsOpen] = React.useState(false);
  const skillsUser = useUsersById(skillsOf === null ? [] : [skillsOf])[0];

  const isAdmin = atLeast(actor?.role, "admin");
  const back = (
    <Link to="/teams" className="mb-3 inline-flex items-center gap-1 text-xs text-text-muted hover:text-text">
      <ArrowLeft className="size-3.5" aria-hidden />
      Teams
    </Link>
  );

  if (team.isPending) {
    return (
      <Page>
        {back}
        <Card>
          <TableSkeleton cols={4} />
        </Card>
      </Page>
    );
  }
  if (team.error || !team.data) {
    return (
      <Page>
        {back}
        <ErrorState error={team.error} title="Could not load the team" />
      </Page>
    );
  }
  const t = team.data;
  // Deleting a team that staffs lines takes its people off them: an admin's, as staffing is.
  const canDelete = isAdmin || t.lines.length === 0;

  return (
    <Page>
      {back}
      <PageHeader
        title={
          <span className="inline-flex items-center gap-2">
            <Hash className="size-5 text-text-muted" aria-hidden />
            {t.name}
          </span>
        }
        description={teamSummary(t)}
        action={
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" aria-label="Team actions">
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => setEditing(true)}>
                <Pencil />
                Edit the team
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setDeleting(true)} disabled={!canDelete}>
                <Trash2 />
                Delete the team
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        }
      />

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <Card className="overflow-hidden">
          <CardHeader
            title="People"
            description="Everyone with a phone is on each line the team staffs."
            action={
              <Button variant="outline" size="sm" onClick={() => setAdding(true)}>
                <Plus />
                Add people
              </Button>
            }
          />
          {t.members.length === 0 ? (
            <EmptyState icon={Users} title="Nobody in the team yet" description="Add the people who work together." />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Person</TableHead>
                  <TableHead>Skills</TableHead>
                  <TableHead>Other teams</TableHead>
                  <TableHead className="w-40" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {t.members.map((m) => {
                  const missing = missingSkills(m, t.lines, catalog.data ?? []);
                  const skills = Object.entries(m.skills).sort(([a], [b]) => a.localeCompare(b));
                  return (
                    <TableRow key={m.user_id}>
                      <TableCell>
                        <span className="flex items-center gap-2.5">
                          <Avatar name={personName(m)} className="size-7" />
                          <span className="min-w-0">
                            <span className="flex items-center gap-1.5 text-sm text-text">
                              {personName(m)}
                              {!m.is_active && (
                                <Badge tone="outline" size="sm">
                                  Deactivated
                                </Badge>
                              )}
                            </span>
                            <span className="block font-mono text-2xs text-text-subtle">
                              {m.extension ? `ext. ${m.extension}` : "No phone: on no line"}
                            </span>
                          </span>
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="flex flex-wrap items-center gap-1">
                          {skills.length === 0 && <span className="text-xs text-text-subtle">None</span>}
                          {skills.map(([slug, level]) => (
                            <Badge key={slug} tone="outline" size="sm" className="font-normal text-text">
                              {catalog.data?.find((s) => s.slug === slug)?.name ?? slug} {level}
                            </Badge>
                          ))}
                        </span>
                        {missing.map((x) => (
                          <span key={x.slug} className="mt-1 flex items-center gap-1 text-2xs text-warning-11">
                            <AlertTriangle className="size-3 shrink-0" aria-hidden />
                            {missingText(x, m.skills[x.slug])}: not offered calls on {x.lines.join(", ")}
                          </span>
                        ))}
                      </TableCell>
                      <TableCell className="text-xs text-text-muted">
                        {m.other_teams.length === 0 ? "—" : m.other_teams.join(", ")}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="inline-flex items-center gap-1">
                          {m.extension !== null && (
                            <Button
                              variant={missing.length > 0 && isAdmin ? "default" : "ghost"}
                              size="sm"
                              onClick={() => {
                                setSkillsOf(m.user_id);
                                setSkillsOpen(true);
                              }}
                            >
                              {isAdmin ? "Edit skills" : "Skills"}
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={`Take ${personName(m)} out of the team`}
                            onClick={() => setLeaving(m)}
                          >
                            <X />
                          </Button>
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Card>

        <Card>
          <CardHeader
            title="Staffs"
            description={
              isAdmin
                ? "The lines this team is on. Add one from the campaign's Staffing tab."
                : "The lines this team is on. An admin puts a team on a campaign's line."
            }
          />
          <CardBody className="space-y-1.5">
            {t.lines.length === 0 && <p className="text-xs text-text-muted">No line yet.</p>}
            {t.lines.map((l) => (
              <div key={l.queue_id} className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2">
                <span className="min-w-0">
                  {l.campaign_id ? (
                    <Link
                      to={`/campaigns/${l.campaign_id}?tab=staffing`}
                      className="block truncate text-sm text-text hover:text-accent-11"
                    >
                      {l.campaign_name ?? l.queue_name}
                    </Link>
                  ) : (
                    <span className="block truncate text-sm text-text">{l.queue_name}</span>
                  )}
                  <span className="block font-mono text-2xs text-text-subtle">
                    {l.queue_name} · {onLineCount(t)} on it
                  </span>
                </span>
                <span className="inline-flex items-center gap-1">
                  <Badge tone="neutral" size="sm">
                    {linePurposeText(l.purpose).label}
                  </Badge>
                  {isAdmin && (
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Take ${t.name} off ${l.queue_name}`}
                      onClick={() => setUnstaffing(l)}
                    >
                      <X />
                    </Button>
                  )}
                </span>
              </div>
            ))}
          </CardBody>
        </Card>
      </div>

      <TeamFormDialog open={editing} onOpenChange={setEditing} team={t} />
      <AddPeopleDialog open={adding} onOpenChange={setAdding} team={t} />
      <UserSkillsDialog
        open={skillsOpen && skillsUser !== undefined}
        onOpenChange={setSkillsOpen}
        user={skillsUser}
        canEdit={isAdmin}
        isSelf={skillsUser !== undefined && skillsUser.id === actor?.id}
      />

      <ConfirmDialog
        open={leaving !== null}
        onOpenChange={(open) => !open && setLeaving(null)}
        title="Take this person out of the team?"
        description={
          <>
            <span className="font-medium">{leaving ? personName(leaving) : ""}</span> leaves {t.name}
            {t.lines.length > 0
              ? " and comes off its lines, except one another team puts them on or somebody added them to on their own."
              : "."}{" "}
            Their skills stay as they are.
          </>
        }
        confirmLabel="Take out"
        destructive
        loading={removeMember.isPending}
        onConfirm={async () => {
          if (!leaving) return;
          try {
            await removeMember.mutateAsync({ teamId: t.id, userId: leaving.user_id });
            toast.success("Taken out of the team", personName(leaving));
            setLeaving(null);
          } catch (e) {
            toast.error("Could not take them out", e instanceof ApiError ? e.message : "Please try again.");
          }
        }}
      />

      <ConfirmDialog
        open={unstaffing !== null}
        onOpenChange={(open) => !open && setUnstaffing(null)}
        title="Take the team off this line?"
        description={
          <>
            Everyone in {t.name} comes off <span className="font-mono">{unstaffing?.queue_name}</span>, except
            anybody another team puts there or somebody added on their own.
          </>
        }
        confirmLabel="Take the team off"
        destructive
        loading={removeLine.isPending}
        onConfirm={async () => {
          if (!unstaffing) return;
          try {
            await removeLine.mutateAsync({ teamId: t.id, lineId: unstaffing.queue_id });
            toast.success("Team taken off the line", unstaffing.queue_name);
            setUnstaffing(null);
          } catch (e) {
            toast.error("Could not take the team off", e instanceof ApiError ? e.message : "Please try again.");
          }
        }}
      />

      <ConfirmDialog
        open={deleting}
        onOpenChange={setDeleting}
        title="Delete this team?"
        description={
          t.lines.length > 0
            ? `${t.name} goes, and its people come off ${t.lines.map((l) => l.queue_name).join(", ")}, except anybody another team puts there or somebody added on their own. Nobody's skills change.`
            : `${t.name} goes. Nobody's lines or skills change.`
        }
        confirmLabel="Delete the team"
        destructive
        loading={deleteTeam.isPending}
        onConfirm={async () => {
          try {
            await deleteTeam.mutateAsync(t.id);
            toast.success("Team deleted", t.name);
            navigate("/teams");
          } catch (e) {
            toast.error("Could not delete the team", e instanceof ApiError ? e.message : "Please try again.");
          }
        }}
      />
    </Page>
  );
}
